import {
  Box,
  Button,
  Header,
  Link,
  Pagination,
  Table,
} from '@cloudscape-design/components';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getFunctions, httpsCallable } from 'firebase/functions';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AddressDisplay from '../../components/Orders/package/addressDisplay';

export default function ShipmentsPage() {
  const navigate = useNavigate();
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const {
    data: { pages = [], pageParams = [] } = {},
    hasNextPage,
    isFetching,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['getShipments'],
    queryFn: async ({ pageParam }) => {
      const getShipments = httpsCallable(getFunctions(), 'listShipmentsV2');
      const { data } = await getShipments({
        pageSize: 20,
        afterId: pageParam,
      });
      return data;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.has_more) return null;
      return lastPage.shipments?.[lastPage.shipments.length - 1]?.id;
    },
  });

  const shipmentsList = useMemo(() => (pages[currentPageIndex - 1]?.shipments || []).map(
    (ship) => {
      const tracker = ship.tracker || {};
      const trackingDetails = tracker.tracking_details || [];
      const firstTrack = trackingDetails[0] || {};
      const latestTrack = trackingDetails[trackingDetails.length - 1] || {};

      const selectedRate = ship.selected_rate || {};
      const carrierDetail = tracker.carrier_detail || {};
      return {
        ...ship,
        shipDate: firstTrack.datetime ? new Date(firstTrack.datetime).toLocaleString() : 'N/A',
        amount: (
          <div style={{ display: 'flex', gap: 8 }}>
            <Box>
              $
              {selectedRate.rate || 'N/A'}
            </Box>
            <Box color="text-status-inactive">{selectedRate.currency?.toUpperCase() || 'N/A'}</Box>
          </div>
        ),
        statusBadge: (
          <div>
            <Box>{latestTrack.message || 'N/A'}</Box>
            <Box color="text-status-inactive">{latestTrack.datetime ? new Date(latestTrack.datetime).toLocaleString() : 'N/A'}</Box>
          </div>
        ),
        shipTo: (
          <AddressDisplay address={ship.to_address} />
        ),
        carrierInfo: (
          <div>
            <Box>{tracker.carrier || 'N/A'}</Box>
            <Box color="text-status-inactive">{carrierDetail.service || 'N/A'}</Box>
            <Box color="text-status-inactive">{carrierDetail.destination_location || 'N/A'}</Box>
          </div>
        ),
        trackingUrl: (
          <Link href={tracker.public_url} external>
            {ship.tracking_code}
          </Link>
        ),
        shippingLabel: ship.postage_label?.label_url ? (
          <Link href={ship.postage_label.label_url} external>
            Shipping Label
          </Link>
        ) : 'N/A',
        updatedOn: ship.updated_at ? new Date(ship.updated_at).toLocaleString() : 'N/A',
      };
    },
  ), [currentPageIndex, pages]);

  return (
    <Table
      header={(
        <Header
          variant="awsui-h1-sticky"
          actions={(
            <Button onClick={() => navigate('/shipments/create')} variant="normal">
              Buy a Shipping Label
            </Button>
          )}
        >
          Shipments
        </Header>
      )}
      empty={(
        <Box
          color="inherit"
          fontWeight="bold"
        >
          No shipments found
        </Box>
      )}
      variant="full-page"
      stickyHeader
      loading={isFetching}
      items={shipmentsList}
      resizableColumns
      wrapLines
      pagination={(
        <Pagination
          currentPageIndex={currentPageIndex}
          onChange={({ detail }) => {
            // only fetchNextPage for new page
            if (detail.currentPageIndex > pageParams.length) {
              fetchNextPage();
            }
            setCurrentPageIndex(detail.currentPageIndex);
          }}
          openEnd={hasNextPage}
          pagesCount={pageParams.length}
        />
      )}
      columnDefinitions={[
        {
          header: 'Ship Date',
          cell: (item) => item.shipDate,
        },
        {
          header: 'Status',
          cell: (item) => item.statusBadge,
        },
        {
          header: 'Amount',
          cell: (item) => item.amount,
        },
        {
          header: 'Ship To',
          cell: (item) => item.shipTo,
        },
        {
          header: 'Carrier',
          cell: (item) => item.carrierInfo,
        },
        {
          header: 'Tracking',
          cell: (item) => item.trackingUrl,
        },
        {
          header: 'Label',
          cell: (item) => item.shippingLabel,
        },
        {
          header: 'Updated At',
          cell: (item) => item.updatedOn,
        },
      ]}
    />
  );
}
